// RECUR-26761 — "simplify paste" for compact inputs (the Comments box).
//
// Pasting rich HTML (e.g. a Fathom meeting-summary email) into a small comment
// box brings two kinds of noise:
//   1. Foreign text styling — the source's own colours / font sizes / families
//      leak onto every leaf. For Fathom the list text inherits the <ul>'s light
//      grey bullet colour (rgb(211,211,211)) and the <li>'s 21px bullet size, so
//      pasted text comes out washed-out and oversized, and links don't look like
//      links (the grey leaf colour overrides the anchor styling).
//   2. Layout scaffolding — the email's <table>/<column> wrappers come through
//      as literal table/column nodes inside the comment.
//
// This pass strips the foreign inline text marks and flattens the layout
// wrappers to plain blocks, while KEEPING the meaningful structure: bold /
// italic / underline / strikethrough marks, links, headings, and lists.

// Inline text-style marks that should NOT survive a paste into a plain comment.
const STRIP_LEAF_MARKS = ['color', 'backgroundColor', 'fontSize', 'fontFamily'] as const;

// Block wrappers that carry no meaning in a comment — unwrap to their content.
const LAYOUT_WRAPPERS = new Set(['table', 'tbody', 'thead', 'tfoot', 'tr', 'td', 'th', 'column_group', 'column']);

function isTextLeaf(n: any): boolean {
    return n != null && typeof n.text === 'string';
}

// Inline = a text leaf or a link (the only inline element type the comments
// editor cares about). Everything else is treated as block-level.
function isInline(n: any): boolean {
    return isTextLeaf(n) || n?.type === 'a';
}

// Strip foreign style marks from a leaf; recurse into inline wrappers (links).
function cleanInline(n: any): any {
    if (isTextLeaf(n)) {
        const out: any = { ...n };
        for (const m of STRIP_LEAF_MARKS) delete out[m];
        return out;
    }
    if (Array.isArray(n?.children)) {
        return { ...n, children: n.children.map(cleanInline) };
    }
    return n;
}

function makeParagraph(inlines: any[]): any {
    return { type: 'p', children: inlines.length ? inlines : [{ text: '' }] };
}

// A block is "pure inline" when all its children are inline (text/links) — the
// common case for <p>/<h2>/list paragraphs. Such a block keeps its type and any
// list marks (listStyleType/indent); only its leaves get cleaned.
function hasOnlyInlineChildren(node: any): boolean {
    return Array.isArray(node?.children) && node.children.every(isInline);
}

/**
 * Flatten + de-style a deserialized paste fragment for a plain comment input.
 * Returns a flat list of block nodes (paragraphs, headings, list paragraphs)
 * with no table/column wrappers and no foreign colour/size/family marks.
 */
export function simplifyPastedFragment(nodes: any[]): any[] {
    const blocks: any[] = [];
    let inlineBuffer: any[] = [];

    const flushInline = () => {
        // Keep the buffer only if it carries a link or non-whitespace text.
        const meaningful = inlineBuffer.some((n) => !isTextLeaf(n) || n.text.trim() !== '');
        if (meaningful) blocks.push(makeParagraph(inlineBuffer));
        inlineBuffer = [];
    };

    for (const node of nodes || []) {
        if (isInline(node)) {
            inlineBuffer.push(cleanInline(node));
            continue;
        }
        // Block-level node — close any pending inline run first.
        flushInline();

        if (node && LAYOUT_WRAPPERS.has(node.type) && Array.isArray(node.children)) {
            // Unwrap the layout wrapper: recurse and lift its content out.
            blocks.push(...simplifyPastedFragment(node.children));
        } else if (node && hasOnlyInlineChildren(node)) {
            // Real block (p / heading / list paragraph): keep it, clean leaves.
            blocks.push({ ...node, children: node.children.map(cleanInline) });
        } else if (node && Array.isArray(node.children)) {
            // Mixed/nested block (e.g. a block that itself contains a table):
            // recurse so any nested wrappers get flattened too.
            blocks.push(...simplifyPastedFragment(node.children));
        } else if (node) {
            blocks.push(node);
        }
    }
    flushInline();

    // Never return an empty fragment (Plate's insertFragment dislikes []).
    return blocks.length ? blocks : [makeParagraph([])];
}
